import { effect, onCleanup, root, signal, untrack, type Accessor, type Dispose, type Signal } from './signal.ts'

/**
 * DOM rendering. Elements are created once; any prop or child given as a function becomes a
 * reactive binding that updates only that attribute or region.
 */

export type Child = Node | string | number | boolean | null | undefined | Child[] | (() => Child)
export type Component<P = Record<string, unknown>> = (props: P) => Child

const SVG_NS = 'http://www.w3.org/2000/svg'
const SVG_TAGS = new Set([
  'svg', 'path', 'g', 'circle', 'ellipse', 'rect', 'line', 'polyline', 'polygon', 'defs',
  'linearGradient', 'radialGradient', 'stop', 'clipPath', 'mask', 'use', 'text', 'tspan', 'filter',
  'feGaussianBlur', 'feOffset', 'feMerge', 'feMergeNode', 'feColorMatrix', 'symbol', 'title',
])
const PROPERTY_KEYS = new Set(['value', 'checked', 'selected', 'indeterminate', 'muted', 'textContent', 'innerHTML', 'scrollTop'])

export const Fragment = Symbol('Fragment')

export function h(type: string | Component<any> | typeof Fragment, props: Record<string, unknown> | null, ...children: Child[]): Child {
  const allProps = { ...(props ?? {}) }
  if (children.length > 0) allProps.children = children.length === 1 ? children[0] : children
  return create(type, allProps)
}

export function create(type: string | Component<any> | typeof Fragment, props: Record<string, unknown>): Child {
  if (type === Fragment) return props.children as Child
  if (typeof type === 'function') return untrack(() => type(props))
  const svg = SVG_TAGS.has(type)
  const element = svg ? document.createElementNS(SVG_NS, type) : document.createElement(type)
  for (const key of Object.keys(props)) {
    if (key === 'children') continue
    applyProp(element, key, props[key], svg)
  }
  if (props.children !== undefined) insert(element, props.children as Child)
  return element
}

function applyProp(element: Element, key: string, value: unknown, svg: boolean): void {
  if (key === 'ref') {
    if (typeof value === 'function') (value as (el: Element) => void)(element)
    return
  }
  if (key.startsWith('on') && key.length > 2 && typeof value === 'function') {
    const capture = key.endsWith('Capture')
    const name = (capture ? key.slice(2, -7) : key.slice(2)).toLowerCase()
    element.addEventListener(name, value as EventListener, capture)
    return
  }
  if (key === 'classList' && value && typeof value === 'object') {
    for (const [name, on] of Object.entries(value as Record<string, unknown>)) {
      if (typeof on === 'function') effect(() => { element.classList.toggle(name, Boolean((on as Accessor<unknown>)())) })
      else element.classList.toggle(name, Boolean(on))
    }
    return
  }
  if (key === 'style' && value && typeof value === 'object') {
    const style = (element as HTMLElement).style
    for (const [name, v] of Object.entries(value as Record<string, unknown>)) {
      if (typeof v === 'function') effect(() => setStyle(style, name, (v as Accessor<unknown>)()))
      else setStyle(style, name, v)
    }
    return
  }
  if (typeof value === 'function') {
    effect(() => setAttribute(element, key, (value as Accessor<unknown>)(), svg))
    return
  }
  setAttribute(element, key, value, svg)
}

function setStyle(style: CSSStyleDeclaration, name: string, value: unknown): void {
  if (value === null || value === undefined || value === false) style.removeProperty(name)
  else if (name.startsWith('--') || name.includes('-')) style.setProperty(name, String(value))
  else (style as unknown as Record<string, string>)[name] = String(value)
}

function setAttribute(element: Element, key: string, value: unknown, svg: boolean): void {
  if (key === 'className') key = 'class'
  if (!svg && PROPERTY_KEYS.has(key)) {
    ;(element as unknown as Record<string, unknown>)[key] = value
    return
  }
  if (key === 'style') {
    if (value === null || value === undefined || value === false) element.removeAttribute('style')
    else element.setAttribute('style', String(value))
    return
  }
  if (value === null || value === undefined || value === false) element.removeAttribute(key)
  else element.setAttribute(key, value === true ? '' : String(value))
}

/** Inserts a child (static or reactive) at the end of `parent`, or before `before`. */
export function insert(parent: Node, child: Child, before: Node | null = null): void {
  if (typeof child === 'function') {
    const start = document.createComment('')
    const end = document.createComment('')
    parent.insertBefore(start, before)
    parent.insertBefore(end, before)
    let previous: Child | typeof UNSET = UNSET
    let nodes: Node[] = []
    // What is inserted lives in its own scope, not the region's: the region re-runs whenever what
    // it read changes, and when it comes back with the same value, the regions nested in that
    // value (a <Show> in a fragment) must stay alive. A new value, or the region's end, disposes it.
    let content: Dispose | null = null
    onCleanup(() => content?.())
    effect(() => {
      const value = (child as () => Child)()
      if (value === previous) return
      previous = value
      content?.()
      content = null
      clearBetween(start, end)
      // Fragments empty themselves on insertion; keep their nodes so a region can re-insert them.
      if (value instanceof DocumentFragment) {
        if (value.childNodes.length > 0) nodes = [...value.childNodes]
        for (const node of nodes) end.parentNode!.insertBefore(node, end)
        return
      }
      nodes = []
      root((dispose) => {
        content = dispose
        insertStatic(end.parentNode!, value, end)
      })
    })
    return
  }
  insertStatic(parent, child, before)
}

const UNSET = Symbol('unset')

function insertStatic(parent: Node, child: Child, before: Node | null): void {
  if (child === null || child === undefined || child === false || child === true) return
  if (Array.isArray(child)) {
    for (const item of child) insert(parent, item, before)
    return
  }
  if (typeof child === 'function') {
    insert(parent, child, before)
    return
  }
  if (child instanceof Node) {
    parent.insertBefore(child, before)
    return
  }
  parent.insertBefore(document.createTextNode(String(child)), before)
}

function clearBetween(start: Node, end: Node): void {
  const parent = start.parentNode
  if (!parent) return
  let node = start.nextSibling
  while (node && node !== end) {
    const next = node.nextSibling
    parent.removeChild(node)
    node = next
  }
}

/** Mounts a component into a container and returns a disposer that removes it. */
export function mount(render: () => Child, container: Element): Dispose {
  return root((dispose) => {
    const start = document.createComment('mount')
    container.appendChild(start)
    const end = document.createComment('/mount')
    container.appendChild(end)
    insert(container, render, end)
    return () => {
      dispose()
      clearBetween(start, end)
      start.remove()
      end.remove()
    }
  })
}

/** Conditional region. `children` receives the truthy value. */
export function Show<T>(props: { when: Accessor<T | null | undefined | false>; fallback?: Child; children: (value: Accessor<T>) => Child }): Child {
  let last: unknown = undefined
  let cached: Child = null
  let disposeBranch: Dispose | null = null
  onCleanup(() => disposeBranch?.())
  // Keep the last truthy value so a binding that is about to be disposed never reads null.
  let lastTruthy: T | undefined
  const value = () => {
    const current = props.when()
    if (current) lastTruthy = current as T
    return lastTruthy as T
  }
  return () => {
    const current = props.when()
    const truthy = Boolean(current)
    if (truthy === Boolean(last) && last !== undefined) return cached
    last = truthy ? true : false
    disposeBranch?.()
    disposeBranch = null
    if (!truthy) {
      cached = props.fallback ?? null
      return cached
    }
    cached = root((dispose) => {
      disposeBranch = dispose
      return untrack(() => props.children(value))
    })
    return cached
  }
}

interface Row<T> {
  key: unknown
  item: Signal<T>
  index: Signal<number>
  first: Node
  last: Node
  dispose: Dispose
}

/** A row's current nodes: everything from its first to its last node, including reactive content. */
function rowNodes(row: Row<unknown>): Node[] {
  const nodes: Node[] = [row.first]
  let node: Node | null = row.first
  while (node && node !== row.last) {
    node = node.nextSibling
    if (node) nodes.push(node)
  }
  return nodes
}

/**
 * Keyed list. Rows persist across updates by key (the item itself without one); each row receives
 * accessors for its item and index so content updates in place (and CSS transitions keep running).
 * A kept row gets the new item, so it reads `item()` in its bindings, not once when it is made.
 */
export function For<T>(props: { each: Accessor<readonly T[]>; key?: (item: T, index: number) => unknown; children: (item: Accessor<T>, index: Accessor<number>) => Child }): Child {
  const container = document.createDocumentFragment()
  const start = document.createComment('for')
  const end = document.createComment('/for')
  container.append(start, end)
  let rows: Row<T>[] = []
  onCleanup(() => {
    for (const row of rows) row.dispose()
    rows = []
  })
  effect(() => {
    const items = props.each() ?? []
    untrack(() => {
      const keyOf = props.key ?? ((item: T) => item)
      const previous = new Map<unknown, Row<T>[]>()
      for (const row of rows) {
        const list = previous.get(row.key)
        if (list) list.push(row)
        else previous.set(row.key, [row])
      }
      const next: Row<T>[] = []
      items.forEach((item, index) => {
        const key = keyOf(item, index)
        const reuse = previous.get(key)?.shift()
        if (reuse) {
          reuse.item.set(item)
          reuse.index.set(index)
          next.push(reuse)
          return
        }
        const itemSignal = signal(item)
        const indexSignal = signal(index)
        const row = root((dispose) => {
          const fragment = document.createDocumentFragment()
          const first = document.createComment('')
          fragment.appendChild(first)
          insert(fragment, props.children(itemSignal, indexSignal))
          const last = fragment.lastChild!
          return { key, item: itemSignal, index: indexSignal, first, last, dispose, fragment }
        })
        next.push(row)
      })
      for (const list of previous.values()) {
        for (const row of list) {
          const nodes = rowNodes(row as Row<unknown>)
          row.dispose()
          for (const node of nodes) node.parentNode?.removeChild(node)
        }
      }
      const parent = end.parentNode
      if (parent) {
        let cursor: Node = start
        for (const row of next) {
          for (const node of rowNodes(row as Row<unknown>)) {
            if (cursor.nextSibling !== node) parent.insertBefore(node, cursor.nextSibling)
            cursor = node
          }
        }
      } else {
        for (const row of next) {
          const fragment = (row as Row<T> & { fragment?: DocumentFragment }).fragment
          if (fragment && fragment.childNodes.length > 0) container.insertBefore(fragment, end)
          else for (const node of rowNodes(row as Row<unknown>)) container.insertBefore(node, end)
        }
      }
      rows = next
    })
  })
  return container
}

/** Tracks a CSS media query as a signal. */
export function media(query: string): Accessor<boolean> {
  const list = window.matchMedia(query)
  const value = signal(list.matches)
  const listener = () => value.set(list.matches)
  list.addEventListener('change', listener)
  onCleanup(() => list.removeEventListener('change', listener))
  return value
}
